// Util functions to manage http calls and automatic refresh

import 'intl';
import 'intl/locale-data/jsonp/en';
import axios from 'axios';

import StatusCodes from 'http-status';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { DateTime } from 'luxon';
import { Platform } from 'react-native';
import { APIPaths } from '@bcm/apis';

import processToken from './processToken.js';
import websocketsActions from '../websocketsActions';
import wse from './websocketEventEmitter.js';
// import rqe from './requestEventEmitter.js';
import env from '../../../constants/env.js';


import { AUTH, REQ } from './actionCases.js';
import { API_METHODS, API_AUTH_TYPES, API_GATEWAY_TYPES, API_HEADER_CONTENT_TYPES, DUPLEX_SERVICES } from '@bcm/constants';
import secureStorage from '../../../src/secureStorage';

// See https://github.com/expo/expo/issues/6536 for this issue.
if (Platform.OS === 'android' && typeof (Intl).__disableRegExpRestore === 'function') (Intl).__disableRegExpRestore();

const maxRetries = 1;

// Init event pub/sub
export const setFailedAutoRefresh = () => ({ type: AUTH.SET_FAILED_AUTO_REFRESH });

export const refresh = async (dispatch) => {
	const res = await req(APIPaths.authRefresh, {});
	// Check for server failure
	if (!res || res.status !== StatusCodes.OK) {
		// console.log('Refresh failed:', res.message);
		await dispatch(setFailedAutoRefresh());
		return res;
	}
	const userData = JSON.parse(await AsyncStorage.getItem('userData'));
	const refreshedUserData = {
		...userData,
		failedAutoLogin: false,
		failedAutoRefresh: false,
	};
	// Save user and connect attempt to open websocket
	await AsyncStorage.setItem('userData', JSON.stringify(refreshedUserData));
	await secureStorage.setItem('idToken', res.data.AuthenticationResult.IdToken);
	await dispatch(websocketsActions.onConnect(res.data.AuthenticationResult.IdToken));
	return res;
};

// Route call to websocket or rest api based authType
const req = async (pathObj, event) => {
	try {
		// Extract pathObj vars
		const authType = pathObj.authorization;
		let paramsObj = (event.params) ? {} : undefined;
		if(event.params) {
			for (const param of pathObj.params) {
				paramsObj[param] = event.params[param];
			}
		}

		// Extract user token
		const cachedIdToken = await secureStorage.getItem('idToken');
		const cachedRefreshToken = await secureStorage.getItem('refreshToken');

		let headers = {};
		let isExpired;
		let idToken;
		if(authType === API_AUTH_TYPES.refresh && cachedRefreshToken) headers.Authorization = cachedRefreshToken;
		else if(authType === API_AUTH_TYPES.refresh) return;
		else if(authType === API_AUTH_TYPES.idToken && !cachedIdToken) {
			await refresh(event.dispatch);
			const idToken = await secureStorage.getItem('idToken');
			headers.Authorization = `Bearer ${idToken}`;
		}
		else if(authType === API_AUTH_TYPES.idToken && cachedIdToken) {
		// Check expiration date of login token
			const processedIdToken = processToken(cachedIdToken);
			const now = DateTime.now().toSeconds();
			isExpired = processedIdToken.exp < now;

			if(isExpired) await refresh(event.dispatch);
			headers.Authorization = `Bearer ${cachedIdToken}`;
			idToken = cachedIdToken;
		}
		let res;
		if(authType === API_AUTH_TYPES.noAuth
		|| authType === API_AUTH_TYPES.refresh
		|| pathObj.gatewayPriority === API_GATEWAY_TYPES.rest
		|| env.DUPLEX_SERVICE === DUPLEX_SERVICES.polling) {
			let query = '';
			if(event && event.query) {
				for (const queryParam of Object.keys(event.query)) {
					const value = event.query[queryParam];
					// Check for query params that are undefined and blank
					if(value !== '' && value !== undefined) {
					// Stringify objects and encode values as a URI component
						const encodedValue = (typeof value === 'object') ? encodeURIComponent(JSON.stringify(value)) : encodeURIComponent(value);
						if(query === '') query += `?${queryParam}=${encodedValue}`;
						else query += `&${queryParam}=${encodedValue}`;
					}
				}
			}
			const pathWithParams = (event.params) ? pathObj.RESTPathBuild(event.params) : pathObj.RESTPath;
			res = await restApiReq(headers, pathObj.method, authType, event, pathWithParams, query, pathObj.contentType);
		}
		else {
			let query = {};
			if(event && event.query) {
				for (const queryParam of Object.keys(event.query)) {
					const value = event.query[queryParam];
					// Check for query params that are undefined and blank
					if(value !== '' && value !== undefined) {
					// Stringify objects and encode values as a URI component
						const encodedValue = (typeof value === 'object') ? encodeURIComponent(JSON.stringify(value)) : encodeURIComponent(value);
						query[queryParam] = encodedValue;
					}
				}
			}
			try {
				res = await websocketReq(idToken, isExpired, event, pathObj.WSRoute, query, paramsObj);
			}
			catch (err) {
				console.log(err);
				res = {
					statusCode: StatusCodes.INTERNAL_SERVER_ERROR,
					message: err,
				};
			}
		}
		return res;
	}
	catch (err) {
		console.log('Req Error', err);
		return {
			statusCode: StatusCodes.INTERNAL_SERVER_ERROR,
			message: err,
		};
	}
};

/**
 * A websocket req doesn't receive a response like a rest API call. It MAY receive a call pushed by the server
 * This function forces a websocket's synchronous emit/listen nature to behave like an async/await rest API call
 * It does this by returning a promise which resolves on hearing an emited event from the wse pub/sub object in local memory
 * The wse object also matches requests to anonymous incoming messages by sending and checking the reqId
 * Is uses a promise.race. If the timeout function complete first, the call effectively fails and stops tying up resources
 **/
const getPromiseFromEvent = (reqId, timeout = parseInt(env.WEBSOCKET_REST_TIMEOUT)) => {
	const websocketPromise = new Promise((resolve) => {
		const listener = (data) => {
			resolve(data);
		};
		wse.subscribe(reqId, listener);
	});
	const timeoutPromise = new Promise((resolve) => {
		const err = {
			data: {
				message: `Request: ${reqId} timed out within ${timeout} ms`,
				statusCode: StatusCodes.REQUEST_TIMEOUT,
			}
		};
		setTimeout(() => {
			wse.unsubscribe(reqId); // Clean timeout reqId from wse
			resolve(err);
		}, timeout);
	});
	return Promise.race([websocketPromise, timeoutPromise]).then((res) => res.data);
};

const websocketReq = async (idToken, wasExpired, event, action, query, pathParameters = null) => {
	// Construct the call body:
	const now = DateTime.now().toSeconds();
	const reqId = `${action}-${now}-${Math.random()}`;
	const params = {
		reqType: API_GATEWAY_TYPES.websocket,
		reqId,
		idToken,
		action,
		query,
		pathParameters,
	};
	if(event.body) params.body = event.body;
	// Check for connection/expired connection and reconnect
	let res;
	let localWs = wse.ws;

	// Attempts to refresh token/connection if expired
	if(!localWs || wasExpired) {
		const connectReqId = `connect-${now}`;
		localWs = new WebSocket(`${env.WEBSOCKET_API_GATEWAY}?authorization=${idToken}`);
		localWs.onopen = () => wse.publish(connectReqId, true);
		const isConnected = await getPromiseFromEvent(connectReqId);
		if(!isConnected) {
			const err = new Error(`Request: ${connectReqId} timed out`);
			err.statusCode = StatusCodes.REQUEST_TIMEOUT;
			return;
		}
		localWs = websocketsActions.initWsFunctions(localWs, event.dispatch);
		wse.setWs(localWs);
	}
	// console.log('WS Req:', params);
	if(localWs.readyState === WebSocket.OPEN) await localWs.send(JSON.stringify(params));
	else {
		// If NOT OPEN, retry resending the request N times at X intervals
		const send = async (count) => {
			if(count >= 10) return;
			if(localWs.readyState === WebSocket.OPEN) await localWs.send(JSON.stringify(params));
			else setTimeout(() => send(count + 1), 500);
		};
		setTimeout(() => send(0), 500);
	}
	const timeout = (event.timeout) ? parseInt(event.timeout) : parseInt(env.WEBSOCKET_REST_TIMEOUT);
	res = await getPromiseFromEvent(reqId, timeout);
	// console.log('WS Res', res);
	return res;
};

const validFileNames = ['data', 'bodySiteFront', 'bodySiteBack', 'pdf', 'image', 'video'];
const restApiReq = async (headers, method, authType, event, path, query, contentType) => {
	let params = {
		url: `${env.REST_API_GATEWAY}${path}${query}`,
		method,
		timeout: parseInt(env.WEBSOCKET_REST_TIMEOUT),
		headers,
	};
	// if data is a photo, transform to photo formData
	if (contentType === API_HEADER_CONTENT_TYPES.multipartFormData && event.body) {
		let formData = new FormData();
		const processFile = (data, param) => {
			const fileTypeString = data.type.toLowerCase().split('/')[1];
			let fileType;
			if(fileTypeString === 'png') fileType = 'png';
			if(fileTypeString === 'jpg') fileType = 'jpg';
			if(fileTypeString === 'jpeg') fileType = 'jpeg';
			if(fileTypeString === 'pdf') fileType = 'pdf';
			if(fileTypeString === 'mp4') fileType = 'mp4';
			const file = new File([data], `${param}.${fileType}`);
			formData.append(param, file);
		};
		for (const param in event.body) {
			const data = event.body[param];
			const processArray = (data) => {
				const filePathArray = data.split('.');
				const imageType = filePathArray[filePathArray.length -1];
				formData.append(param, {
					uri: data,
					name: `${param}.${imageType}`,
					type: `image/${imageType}`
				});
			};
			if( validFileNames.includes(param) && Array.isArray(data)) {
				if (typeof data[0] === 'string') {
					for (const file of data) {
						processArray(file);
					}
				} else {
					for (const file of data) {
						processFile(file, param);
					}
				}
			}
			else if( validFileNames.includes(param) && typeof data === 'object') {
				processFile(data, param);
			}
			else if(validFileNames.includes(param) && typeof data === 'string') {
				processArray(data);
			}
			else {
				const stringBody = typeof event.body[param] === 'string' ? event.body[param] : JSON.stringify(event.body[param]);
				formData.append(param, stringBody);
			}
		}
		event.body = formData;
		params.headers = {
			...params.headers,
			'Content-Type': 'multipart/form-data',
		};
	}
	if (event.body) params.data = event.body;
	try {
		// console.log('Rest API Req', params);
		const res = await axios(params);
		// console.log('Rest API Res', res);
		return {
			status: (res.status) ? res.status : res.statusCode,
			data: (res.data && res.data.body && typeof res.data.body === 'string') ? JSON.parse(res.data.body) : res.data.body
		};
	}
	catch (err) {
		let errorRes = (err.response) ? JSON.parse(JSON.stringify(err.response)): err;
		// Check for expired idToken causing a 500 failure
		if(err.response
      && err.response.status === StatusCodes.UNAUTHORIZED
      // && path !== '/auth/refresh'
      && params.retryCount < maxRetries) {
			await refresh(event.dispatch);
			return await req(method, authType, path, {...event, retryCount: (params.retryCount) ? params.retryCount ++ : 1});
		}
		return errorRes;
	}
};

export const getS3Object = async (path) => {
	try {
		const res = await axios({
			url: path,
			method: API_METHODS.get
		});
		return res.data;
	} catch (err) {
		console.log(err);
	}
};

export const postS3Object = async (dispatch, name, filePath, signedUrl) => {

	try {
		const resp = await fetch(filePath);
		const blob = await resp.blob();

		// Use XMLHttpRequest to track progress, since fetch doesn't support progress tracking
		// Can't use axios as it doesn't read the blob file properly.
		const xhr = new XMLHttpRequest();

		return new Promise((resolve, reject) => {
			xhr.open('PUT', signedUrl, true);

			xhr.upload.onprogress = (event) => {
				if (event.lengthComputable) {
					const percentComplete = (event.loaded / event.total) * 100;
					if (percentComplete < 100) dispatch({
						type: REQ.POST,
						name,
						percentComplete,
						error: null,
					});
					else if (percentComplete === 100) dispatch({
						type: REQ.DELETE,
						name,
					});
				}
			};

			xhr.onload = () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					console.log('Upload complete:', xhr.response);
					resolve(xhr.response);
				} else {
					reject(new Error('Upload failed with status: ' + xhr.status));
				}
			};

			xhr.onerror = () => {
				dispatch({
					type: REQ.POST,
					name,
					error: xhr.statusText,
				});
				reject(new Error('Upload failed: ' + xhr.statusText));
			};

			xhr.send(blob);
		});
	} catch (error) {
		console.error('Error:', error);
	}
};

export default req;